import React, { useEffect, useState } from 'react';
import { Dialog, DialogSurface, DialogBody, DialogTitle, Button, Input } from '@fluentui/react-components';
import { Dismiss24Regular, ArrowClockwise24Regular, ArrowRight24Regular } from '@fluentui/react-icons';
import { Tender } from '../../../models/tendering';
import { createDocument } from '../../../services/documentService';
import { useAppContext } from '../../../AppContext';
import { updateTenderDocument } from '../../../services/tenderingService';
import { deepCloneObject } from '../../../utils/clone';

interface ContactCodeProps {
  open: boolean;
  code: string;
  tender: Tender;
  isEmail: boolean;
  isPhone: boolean;
  newValue: string;
  onClose: () => void;
  onVerified: (tender: Tender) => void;
}

export const ContactCode: React.FC<ContactCodeProps> = ({
  open,
  code,
  isEmail,
  isPhone,
  tender,
  newValue,
  onClose,
  onVerified,
}) => {
  const {user} = useAppContext();


  const [showNoCodeButtons, setShowNoCodeButtons] = useState<boolean>(false);
  const [dialogCode, setDialogCode] = useState<string>('');

  useEffect(() => {
    if(open){
      setDialogCode('');
      setShowNoCodeButtons(false);
    }
  }, [open]);

  const handleInputChange = (value: string) => {
    // Only allow digits and max 6 characters
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setDialogCode(numericValue);
  };

  const handleVerify = async () => {
    if (dialogCode.length === 6 && dialogCode === code) {
      let updatedTender = deepCloneObject(tender);
      if(isEmail){
        updatedTender.contact = {
          ...updatedTender.contact,
          email: newValue
        };
      }
      if(isPhone){
        updatedTender.contact = {
          ...updatedTender.contact,
          phone: newValue
        };
      }

      try{
        await updateTenderDocument(tender.tenderID, updatedTender);
        setDialogCode('');
        onVerified(updatedTender);
        onClose();
      }
      catch(error){
        console.error('Error updating tender contact after code verification:', error);
        onVerified(updatedTender);
      }
    }
  };

  const resendCode = async () => {
    const documentData = {
      moduleID: tender.tenderID,
      notificationType: isEmail ? 1 : 2,
      question: `Förfrågningar kontaktuppgifter (${isEmail ? 'e-post' : 'mobiltelefon'})`,
      new: {
        value: isEmail ? tender.contact?.email : tender.contact?.phone,
        question: `Förfrågningar kontaktuppgifter (${isEmail ? 'e-post' : 'mobiltelefon'})`
      },
      old: {
        value: isEmail ? tender.contact?.email : tender.contact?.phone,
        question: `Förfrågningar kontaktuppgifter (${isEmail ? 'e-post' : 'mobiltelefon'})`
      },
      notifyUser: !user?.userType?.system
    };
  
    const docResponse = await createDocument(documentData);
  }

  const updateAnyway = async () => {
    let updatedTender = deepCloneObject(tender);
    if(isEmail){
      updatedTender.contact = {
        ...updatedTender.contact,
        email: newValue
      };
    }
    if(isPhone){
      updatedTender.contact = {
        ...updatedTender.contact,
        phone: newValue
      };
    }

    try{
      await updateTenderDocument(tender.tenderID, updatedTender);
      onVerified(updatedTender);
      onClose();
    }
    catch(error){
      console.error('Error updating tender contact after code verification:', error);
      onVerified(updatedTender);
    }
  }

  const isCodeComplete = dialogCode.length === 6;

  return (
    <Dialog modalType='alert' open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <div className="flex items-start justify-between mb-6">
          <DialogTitle className="text-xl font-semibold text-gray-800">
            Bekräfta ändring
          </DialogTitle>
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={onClose}
            className="hover:bg-gray-100"
          />
        </div>
        <div className="mb-6">
          <p className="text-sm text-gray-700 leading-relaxed">
            För att säkerställa din identitet kommer du att få en kod skickad till dig via
            e-post eller SMS. Ange denna kod i det tillåtna fältet för att slutföra
            processen. Koden är personlig och giltig under en begränsad tid. Om du inte
            har fått koden inom några minuter, kontrollera din skräppostmapp eller
            försök igen.
          </p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Din kod:
          </label>
          <div className="mb-6">
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={dialogCode}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="000000"
            />
          </div>
          <Button
            appearance="primary"
            onClick={handleVerify}
            disabled={!isCodeComplete}
            className="w-auto px-8 py-2 text-white font-medium rounded"
          >
            Verifiera kod
          </Button>
        </div>
        <div className="mb-6">
          <button
            className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline"
            onClick={() => setShowNoCodeButtons(true)}
          >
            Inte fått någon kod?
          </button>
        </div>
        { showNoCodeButtons && <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            appearance="outline"
            icon={<ArrowClockwise24Regular />}
            onClick={() => resendCode()}
            className="flex-1 border-gray-300 hover:bg-gray-50"
          >
            Skicka kod igen
          </Button>
          <Button
            appearance="outline"
            icon={<ArrowRight24Regular />}
            onClick={() => updateAnyway()}
            className="flex-1 border-gray-300 hover:bg-gray-50"
          >
            Fortsätt utan kod
          </Button>
        </div>}
      </DialogSurface>
    </Dialog>
  );
};
